import useAuditTrail from "./useAuditTrail";
import { DataTable, FormDateRangePicker, FormInput, FormSectionHeader, FormSelect, Heading, Loader } from "@/components";
import { Button, Card, CardContent } from "@/components/ui";
import { DATE_RANGE, DOCUMENT_TYPE, SEARCH_VALUE, SOURCE_SYSTEM } from "./consts";
import { FileText, Search } from "lucide-react";
import { cn } from "@/lib/utils";
import { FormProvider } from "react-hook-form";

const AuditTrail = () => {
  const {
    breadcrumbData,
    form,
    sourceSystemOptions,
    submissionsColumns,
    onClearAll,
    pageNumber,
    pageSize,
    setPageNumber,
    setPageSize,
    sorting,
    setSorting,
    onSubmit,
    isLoadingData,
    isListLoading,
    documentTypeList,
    quickInsightsData,
    isSubmissionListRefetching,
    stageValue,
    onCardClick,
    // documentType,
    // isSubmitClicked,
    gridData
  } = useAuditTrail();

  return (
    <div className="p-4 md:p-6">
      <div className="flex flex-col lg:flex-row gap-2 lg:items-center lg:justify-between mb-4">
        <div className="flex flex-row items-center justify-between">
          <Heading title="Audit Trail" breadcrumbData={breadcrumbData} />
        </div>
      </div>

      {isListLoading ? <Loader /> : 
        <>
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-7 lg:items-start gap-4">
              <FormSelect
                name={SOURCE_SYSTEM}
                control={form.control}
                label="Source System"
                placeholder="Please select"
                options={sourceSystemOptions}
              />
              {/* <FormSelect
                name={STATUS}
                control={form.control}
                label="Status"
                placeholder="Please select"
                options={statusOptions}
              /> */}
              <FormSelect
                name={DOCUMENT_TYPE}
                control={form.control}
                label="Document Type"
                placeholder="Please select"
                options={documentTypeList?.map((data: any) => ({
                  label: `${data?.documentType} - ${data?.documentTypeDisplayName}`,
                  value: data?.documentTypeId?.toString(),
                }))}
              />
              <div className="lg:col-span-2">
                <FormDateRangePicker
                  name={DATE_RANGE}
                  control={form.control}
                  label="Date Range"
                  placeholder="Please select"
                />
              </div>
              <div className="lg:col-span-3 flex flex-row items-center gap-2">
                <div className="w-[80%]">
                  <FormInput
                    name={SEARCH_VALUE}
                    control={form.control}
                    label=""
                    placeholder="Search"
                    startIcon={<Search className="h-4 w-4" />}
                    className="bg-white"
                  />
                </div>

                <Button>
                  Submit
                </Button>
                <Button
                  variant="secondary"
                  className="border bg-white"
                  onClick={onClearAll}
                >
                  Clear All
                </Button>
              </div>
            </div>
          </form>
        </FormProvider>
      
        {isLoadingData  ? <Loader /> :
          <>
            <Card className="bg-white border border-g-200 rounded-[10px] py-4 mt-4">
              <CardContent className="px-4">
                <FormSectionHeader wrapperClassName="mb-1" className="text-[18px] font-medium">Quick Insights</FormSectionHeader>
                <div className="text-[14px] mb-5 text-g-300">
                  Detailed breakdown by processing stage
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
                  {quickInsightsData?.map((item: any) => {
                    return (
                      <div
                        key={item.title}
                        className={cn("cursor-pointer rounded-[8px] overflow-hidden border", item?.wrapperClassName, item.stage == stageValue ? "shadow-md" : "opacity-[0.7] border-opacity-[0.6]")}
                        onClick={onCardClick(item.stage)}
                      >
                        <div className={cn("flex flex-row items-center gap-4 px-3 py-2 h-[70px]", item?.headerClassName)}>
                          <div
                            className={cn("p-3 rounded-full flex items-center justify-center text-white", item?.iconClassName)}
                          >
                            <FileText className="h-[20px] w-[20px]" />
                          </div>
                          <div className="flex flex-col">
                            <span
                              className={cn("text-[14px] leading-[18px] font-semibold", item?.titleClassName)}
                            >
                              {item?.title}
                            </span>
                            {item?.hasSplit ? <span className={cn("text-[14px] text-g-400")}>
                              Total {item?.data?.total?.value || '0'}
                            </span> : <></>}
                          </div>
                        </div>

                        <div className={cn("px-3 text-[14px] text-g-500", item?.bodyClassName)}>
                          {
                            Object.entries(item?.data)?.map(([key, val]: any, idx: number) => {
                              if (item?.hasSplit && key == "total") return <></>;
                              return (
                                <div className={cn("flex flex-row items-center justify-between gap-2 border-g-200/40 py-1", idx != item?.data?.length ? "" : "border-b", key == "total" ? "h-[88px] items-start" : "")}>
                                  <span>{val?.label}</span>
                                  <Button
                                    type="button"
                                    variant="link"
                                    className="text-black"
                                  >
                                    {val?.value || 0}
                                  </Button>
                                </div>
                              )
                            })
                          }
                          
                          {/* <div className="flex flex-row items-center justify-between gap-2 py-1">
                            <span>Fail</span>
                            <Button
                              type="button"
                              variant="link"
                              className="text-black"
                            >
                              {item?.failed || 0}
                            </Button>
                          </div> */}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </CardContent>
            </Card>

            <div className="mt-4">
              <Card className="bg-white border border-g-200 rounded-[10px] py-4">
                <CardContent className="px-4">
                  <FormSectionHeader className="text-primary">{quickInsightsData?.find((item: any) => item.stage == stageValue)?.title || ""}</FormSectionHeader>
                  {isSubmissionListRefetching ? <Loader /> : 
                    <DataTable className="shadow-none rounded-none border-0"
                      columns={submissionsColumns}
                      data={gridData?.data || []}
                      setPageNumber={setPageNumber}
                      setPageSize={setPageSize}
                      pageNumber={pageNumber}
                      pageSize={pageSize}
                      totalPages={Math.ceil(gridData?.total / pageSize)}
                      totalRecords={gridData?.total}
                      tableWrapperClassName="px-0"
                      headerClass={{
                        row: "border-g-200",
                        cell: "text-primary",
                      }}
                      serverSidePagination={true}
                      sorting={sorting}
                      onSortingChange={setSorting}
                    /> 
                  }
                </CardContent>
              </Card>
            </div>
          </>  
          // <div className="flex items-center justify-center mt-10">
          //   <NoData />
          // </div>
        }
        </>}
    </div>
  );
};

export default AuditTrail;
